/**
 * PDF Export utility for Pace Calculator
 * Uses dynamic imports to lazy-load jspdf and jspdf-autotable
 */

export interface PdfLegData {
	displayName: string
	distance: string
	sectionTime: string
	totalTime: string
}

export interface PdfPaceData {
	type: string
	pace: string
	unit: string
}

export interface PdfModeTotalData {
	label: string
	distance: string
	time: string
}

export interface PdfExportOptions {
	mapName: string
	routeLabel: string
	legs: PdfLegData[]
	paces: PdfPaceData[]
	startTime: string | null
	totalElapsedTime: string | null // Total elapsed time when in clock mode
	totalDistance: string | null
	modeTotals: PdfModeTotalData[]
	generatedAt: Date
}

/**
 * Generate a PDF with pace calculator results
 * Lazy-loads jspdf and jspdf-autotable on demand
 */
export async function generatePacePdf(options: PdfExportOptions): Promise<void> {
	const { mapName, routeLabel, legs, paces, startTime, totalElapsedTime, totalDistance, modeTotals, generatedAt } = options

	// Dynamically import jspdf and autotable
	const jspdfModule = await import('jspdf')
	const autoTableModule = await import('jspdf-autotable')

	const jsPDF = jspdfModule.default
	const autoTable = autoTableModule.default

	const doc = new jsPDF({
		orientation: 'portrait',
		unit: 'mm',
		format: 'a4'
	})

	const pageWidth = doc.internal.pageSize.getWidth()
	const margin = 20
	let yPos = margin

	// Title - Map name
	doc.setFontSize(20)
	doc.setFont('helvetica', 'bold')
	doc.text(mapName || 'Pace Calculator', margin, yPos)
	yPos += 10

	// Subtitle - Route label
	if (routeLabel) {
		doc.setFontSize(14)
		doc.setFont('helvetica', 'normal')
		doc.text(routeLabel, margin, yPos)
		yPos += 8
	}

	// Start time if provided
	if (startTime) {
		doc.setFontSize(11)
		doc.setFont('helvetica', 'normal')
		doc.text(`Start time: ${startTime}`, margin, yPos)
		yPos += 8
	}

	// Paces summary
	if (paces.length > 0) {
		doc.setFontSize(11)
		doc.setFont('helvetica', 'normal')
		const pacesText = paces.map(p => `${p.type}: ${p.pace} ${p.unit}`).join('  •  ')
		doc.text(pacesText, margin, yPos)
		yPos += 12
	}

	// Table header based on whether we have clock time or total time
	const lastColumnHeader = startTime ? 'Clock' : 'Elapsed'

	// Build table body
	const tableBody = legs.map(leg => [
		leg.displayName,
		leg.distance,
		leg.sectionTime,
		leg.totalTime
	])

	// Add total elapsed time row
	if (totalElapsedTime) {
		tableBody.push([
			{ content: 'Total', styles: { fontStyle: 'normal', textColor: [100, 100, 100] } } as any,
			{ content: totalDistance ?? '', styles: { fontStyle: 'normal', textColor: [100, 100, 100], halign: 'right' } } as any,
			{ content: '', styles: { fontStyle: 'normal' } } as any,
			{ content: totalElapsedTime, styles: { fontStyle: 'bold', halign: 'right' } } as any
		])
	}

	// Add per-mode sub-total rows (only when multiple modes exist)
	if (modeTotals.length > 1) {
		for (const mt of modeTotals) {
			tableBody.push([
				{ content: `Total ${mt.label}`, styles: { fontStyle: 'normal', textColor: [130, 130, 130], cellPadding: { left: 8, top: 4, right: 4, bottom: 4 } } } as any,
				{ content: mt.distance, styles: { fontStyle: 'normal', textColor: [130, 130, 130], halign: 'right' } } as any,
				{ content: '', styles: { fontStyle: 'normal', textColor: [130, 130, 130] } } as any,
				{ content: mt.time, styles: { fontStyle: 'normal', textColor: [130, 130, 130], halign: 'right' } } as any
			])
		}
	}

	// Generate table using autotable
	autoTable(doc, {
		startY: yPos,
		head: [[
			'Leg',
			{ content: 'Distance', styles: { halign: 'right' } },
			{ content: 'Time', styles: { halign: 'right' } },
			{ content: lastColumnHeader, styles: { halign: 'right' } }
		]],
		body: tableBody,
		margin: { left: margin, right: margin },
		styles: {
			fontSize: 10,
			cellPadding: 4
		},
		headStyles: {
			fillColor: [66, 66, 66],
			textColor: [255, 255, 255],
			fontStyle: 'bold'
		},
		alternateRowStyles: {
			fillColor: [245, 245, 245]
		},
		columnStyles: {
			0: { cellWidth: 'auto' },
			1: { halign: 'right' },
			2: { halign: 'right' },
			3: { halign: 'right' }
		}
	})

	// Footer with generation timestamp
	// @ts-expect-error - autoTable adds lastAutoTable to doc
	const finalY = doc.lastAutoTable.finalY + 10
	doc.setFontSize(9)
	doc.setTextColor(128, 128, 128)
	const dateStr = generatedAt.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	})
	doc.text(`Generated on ${dateStr} • racedaymap.com`, margin, finalY)

	// Generate filename from map name
	const sanitizedName = (mapName || 'pace-times')
		.replace(/[^a-zA-Z0-9\s-]/g, '')
		.replace(/\s+/g, '-')
		.toLowerCase()
	const filename = `${sanitizedName}-pace-times.pdf`

	// Trigger download
	doc.save(filename)
}
